/**
 * Event Closeout Wizard — Iter-224a
 *
 * Hard-gated 6-step wizard mounted at /admin/operations/closeout/:shiftId.
 *
 * Steps (per user spec):
 *   1. Review Attendance
 *   2. Approve Hours + Payroll
 *   3. Generate Invoice
 *   4. Send Invoice
 *   5. Mark Event Closed   (event-level when shift belongs to event_series)
 *   6. Super-admin Reopen   (audit-required)
 *
 * Constraints from user:
 *   • Hard gate — can't advance until previous step is "done"
 *   • Auto-detect — steps already complete in the DB show green from the start
 *   • Hybrid — steps 1–4 are per-shift, step 5 is event-level
 *   • NO recruiting/applicant/invite info anywhere on this screen
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, CheckCircle2, Circle, Loader2, AlertTriangle, FileText, Send,
  Lock, Unlock, ClipboardCheck, DollarSign, BookCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const STEP_DEFS = [
  { key: 'attendance_approved', title: 'Review Attendance', icon: ClipboardCheck,
    desc: 'Verify all talents have clocked in and out. Reject or adjust any incorrect entries before approving.',
    action: 'approve-attendance', cta: 'Approve Attendance' },
  { key: 'payroll_approved', title: 'Approve Hours & Pay', icon: DollarSign,
    desc: 'Confirm hours worked match what each talent is owed. Hours and pay are signed off together.',
    action: 'approve-payroll', cta: 'Approve Pay' },
  { key: 'invoice_generated', title: 'Generate Invoice', icon: FileText,
    desc: 'Build the invoice from approved hours. Re-running is safe — it only re-stamps the milestone.',
    action: 'generate-invoice', cta: 'Generate Invoice' },
  { key: 'invoice_sent', title: 'Send Invoice', icon: Send,
    desc: 'Email the invoice to the client with the PDF attached.',
    action: 'send-invoice', cta: 'Send Invoice' },
  { key: 'event_closed', title: 'Mark Event Closed', icon: BookCheck,
    desc: 'Final step. Marks the event reconciled. For multi-slot events, all sibling shifts must reach Send Invoice before this is allowed.',
    action: 'close', cta: 'Mark Closed', requiresNote: true },
];

const BADGE_FOR_NEXT = {
  attendance_approved: { label: 'Pending Attendance', color: 'bg-amber-100 text-amber-700' },
  payroll_approved:    { label: 'Ready for Payout',  color: 'bg-blue-100 text-blue-700' },
  invoice_generated:   { label: 'Ready to Invoice',   color: 'bg-cyan-100 text-cyan-700' },
  invoice_sent:        { label: 'Ready to Send',      color: 'bg-violet-100 text-violet-700' },
  event_closed:        { label: 'Ready to Close',     color: 'bg-emerald-100 text-emerald-700' },
  done:                { label: 'Closed',             color: 'bg-gray-200 text-gray-700' },
};

const EventCloseoutWizard = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [closeoutNotes, setCloseoutNotes] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/operations/closeout/${shiftId}`);
      setState(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load closeout state');
    } finally { setLoading(false); }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const isStepDone = (key) => Boolean(state?.steps?.[key]?.done);
  const allStepsDone = state ? STEP_DEFS.every(s => isStepDone(s.key)) : false;

  // Next-pending step determines the dashboard badge label.
  const nextBadge = (() => {
    if (!state) return null;
    if (state.steps.event_closed?.done) return BADGE_FOR_NEXT.done;
    for (const s of STEP_DEFS) {
      if (!isStepDone(s.key)) return BADGE_FOR_NEXT[s.key];
    }
    return BADGE_FOR_NEXT.done;
  })();

  const runStep = async (action, body = null, prevKey = null) => {
    if (prevKey && !isStepDone(prevKey)) {
      toast.error('Complete the previous step first.');
      return;
    }
    setActionBusy(true);
    try {
      const res = await api.post(`/operations/closeout/${shiftId}/${action}`, body ?? {});
      setState(res.data);
      toast.success('Step completed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActionBusy(false); }
  };

  const submitClose = async () => {
    setActionBusy(true);
    try {
      const res = await api.post(`/operations/closeout/${shiftId}/close`, { closeout_notes: closeoutNotes || null });
      setState(res.data);
      setConfirmClose(false);
      toast.success('Event closed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not close event');
    } finally { setActionBusy(false); }
  };

  const submitReopen = async () => {
    if (reopenReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    setActionBusy(true);
    try {
      const res = await api.post(`/operations/closeout/${shiftId}/reopen`, { reason: reopenReason });
      setState(res.data);
      setReopenOpen(false);
      setReopenReason('');
      toast.success('Event reopened.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reopen failed');
    } finally { setActionBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="closeout-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!state) {
    return <div className="p-6 text-center text-gray-500" data-testid="closeout-error">Closeout state unavailable.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4" data-testid="closeout-wizard">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="closeout-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {nextBadge && (
          <Badge className={`ml-auto ${nextBadge.color}`} data-testid="closeout-status-badge">{nextBadge.label}</Badge>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Close Out Event
          </CardTitle>
          <div className="text-xs text-gray-600 space-y-0.5" data-testid="closeout-event-meta">
            <div><strong>Event:</strong> {state.series_name || state.event_title || state.venue_name || 'Untitled event'}</div>
            {state.shift_date && <div><strong>Date:</strong> {formatDateMMDDYYYY(state.shift_date)}</div>}
            {state.venue_name && <div><strong>Venue:</strong> {state.venue_name}</div>}
            {state.role_name && <div><strong>Role:</strong> {state.role_name}</div>}
            {state.series_id && (
              <div className="text-cyan-700">
                Multi-slot event — final close requires all sibling shifts (
                {state.steps.event_closed.sibling_summary?.invoice_sent ?? 0}/
                {state.steps.event_closed.sibling_summary?.total ?? 0} sent)
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="text-xs text-gray-500 pt-0 pb-4">
          {state.stats.bookings_total} booking{state.stats.bookings_total === 1 ? '' : 's'}{' '}
          · {state.stats.bookings_clocked_out} clocked out · {state.stats.hours_approved} hours approved
        </CardContent>
      </Card>

      {/* Progress strip */}
      <div className="flex items-center gap-1" data-testid="closeout-progress-strip">
        {STEP_DEFS.map((s, i) => (
          <div
            key={s.key}
            className={`h-1.5 flex-1 rounded ${isStepDone(s.key) ? 'bg-emerald-500' : 'bg-gray-200'}`}
            data-testid={`closeout-progress-${i}`}
          />
        ))}
      </div>

      {/* Step cards */}
      {STEP_DEFS.map((s, idx) => {
        const Icon = s.icon;
        const done = isStepDone(s.key);
        // Hard gate (user spec #1=a): every prior step must be done,
        // not just the immediate previous. Auto-detected steps still count
        // as done, so this stays compatible with user spec #3=a.
        const allPriorDone = STEP_DEFS.slice(0, idx).every((p) => isStepDone(p.key));
        const locked = !done && !allPriorDone;
        // Hybrid gate — step 5 also needs sibling readiness.
        const siblingBlocked = s.key === 'event_closed' && state.series_id
          && !state.steps.event_closed.sibling_ready;
        const disabled = locked || siblingBlocked || actionBusy;
        const prev = idx === 0 ? null : STEP_DEFS[idx - 1].key;

        return (
          <Card
            key={s.key}
            className={done ? 'border-emerald-300 bg-emerald-50/40' : (locked ? 'opacity-60' : '')}
            data-testid={`closeout-step-${s.key}`}
          >
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`mt-0.5 ${done ? 'text-emerald-600' : (locked ? 'text-gray-300' : 'text-cyan-600')}`}>
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900">Step {idx + 1}: {s.title}</h3>
                </div>
                <p className="text-xs text-gray-600">{s.desc}</p>

                {/* Step-specific extras */}
                {s.key === 'invoice_generated' && state.steps.invoice_generated.invoice_number && (
                  <p className="mt-1.5 text-[11px] text-cyan-700" data-testid="closeout-invoice-number">
                    Invoice <strong>{state.steps.invoice_generated.invoice_number}</strong>
                    {state.steps.invoice_generated.amount_due != null && (
                      <span> — ${Number(state.steps.invoice_generated.amount_due).toFixed(2)} due</span>
                    )}
                    {state.steps.invoice_generated.invoice_id && (
                      <Link
                        to={`/admin/invoices?invoice=${state.steps.invoice_generated.invoice_id}`}
                        className="ml-2 underline"
                        data-testid="closeout-invoice-link"
                      >View →</Link>
                    )}
                  </p>
                )}

                {s.key === 'event_closed' && state.steps.event_closed.notes && (
                  <p className="mt-1.5 text-[11px] text-gray-600 italic" data-testid="closeout-final-notes">
                    Notes: {state.steps.event_closed.notes}
                  </p>
                )}

                {!done && (
                  <div className="mt-3">
                    {s.key === 'event_closed' ? (
                      <Button
                        size="sm"
                        disabled={disabled}
                        onClick={() => setConfirmClose(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="closeout-step-event_closed-btn"
                      >
                        <Lock className="w-3.5 h-3.5 mr-1" /> {s.cta}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={disabled}
                        onClick={() => runStep(s.action, null, prev)}
                        data-testid={`closeout-step-${s.key}-btn`}
                      >
                        {actionBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : s.cta}
                      </Button>
                    )}
                    {siblingBlocked && (
                      <p className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1" data-testid="closeout-sibling-warning">
                        <AlertTriangle className="w-3 h-3" />
                        Waiting for sibling shifts in this event.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Reopen (super-admin only) */}
      {allStepsDone && (
        <div className="pt-2">
          <Button
            variant="outline"
            size="sm"
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
            onClick={() => setReopenOpen(true)}
            data-testid="closeout-reopen-btn"
          >
            <Unlock className="w-3.5 h-3.5 mr-1" /> Reopen Event (super-admin)
          </Button>
          {state.reopen?.reopened_at && (
            <p className="mt-1.5 text-[11px] text-gray-500" data-testid="closeout-reopen-history">
              Previously reopened — {state.reopen.reason}
            </p>
          )}
        </div>
      )}

      {/* Confirm Close dialog */}
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="bg-white max-w-md" data-testid="closeout-close-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Mark event closed?</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              This is the final step. The event will move to the Completed pile and cannot be edited
              by regular admins. Super-admins can reopen with an audit reason.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="closeout-notes">Closeout notes (optional)</Label>
            <Textarea
              id="closeout-notes"
              value={closeoutNotes}
              onChange={(e) => setCloseoutNotes(e.target.value)}
              placeholder="Anything finance or ops should remember about this event."
              className="mt-1"
              rows={3}
              data-testid="closeout-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(false)} disabled={actionBusy}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitClose}
              disabled={actionBusy}
              data-testid="closeout-confirm-close-btn"
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark Closed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="closeout-reopen-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <Unlock className="w-4 h-4" /> Reopen closed event
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Super-admins only. Provide a reason — it's permanent and shows on the audit log.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="reopen-reason">Reason (min 10 chars)</Label>
            <Textarea
              id="reopen-reason"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="e.g., Client reported missing OT line item that needs adjustment."
              className="mt-1"
              rows={3}
              data-testid="closeout-reopen-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)} disabled={actionBusy}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={submitReopen}
              disabled={actionBusy || reopenReason.trim().length < 10}
              data-testid="closeout-confirm-reopen-btn"
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reopen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventCloseoutWizard;
